/**
 * The customer list (F5).
 *
 * A call list, not an inventory. The columns answer *which accounts need
 * someone?* — which is why both owners and the next touch date are columns,
 * and why the default order is the next touch rather than recency.
 *
 * Both owners get a column. PRD §5.5 requires both roles precisely because
 * they are different jobs, and collapsing them into one "Owner" column would
 * lose the distinction the requirement exists to preserve.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 7.3, PRD §5.5
 */
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import type { CustomerSummary } from '@nexus/contracts';

import { RelationshipStageBadge } from '../../components/domain/RelationshipStageBadge';
import { Badge } from '../../components/ui/Badge';
import type { Column } from '../../components/ui/data-table-types';
import { DataTable } from '../../components/ui/DataTable';
import { ExportButton } from '../../components/ui/ExportButton';
import { PageHead } from '../../components/ui/PageHead';
import { Pagination } from '../../components/ui/Pagination';
import { formatDate, formatNumber } from '../../lib/format';
import { customerPath } from '../../lib/routes';
import { useTableState } from '../../lib/use-table-state';

import { useCustomers } from './api/customers.queries';
import { CustomerFilters } from './components/CustomerFilters';

/** Sort fields the API allow-lists (see `CUSTOMER_SORT_FIELDS` on the server). */
const SORT_FIELDS = [
  'name',
  'country',
  'relationshipStage',
  'lastTouchDate',
  'nextTouchDate',
  'createdAt',
  'updatedAt',
];

export function CustomersPage(): React.JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { state, toggleSort, setPage, setPageSize, setFilter, clearFilters } =
    useTableState(SORT_FIELDS);

  const query = useMemo(
    () => ({
      page: state.page,
      pageSize: state.pageSize,
      ...state.filters,
      ...(state.sort === null
        ? {}
        : { sort: `${state.sort.field}:${state.sort.direction}` }),
    }),
    [state],
  );

  const { data, isPending, isError } = useCustomers(query);

  const columns: readonly Column<CustomerSummary>[] = useMemo(
    () => [
      {
        key: 'name',
        header: t('customer.columns.name'),
        cell: (row) => row.name,
        sortField: 'name',
        card: 'primary',
      },
      {
        key: 'stage',
        header: t('customer.columns.stage'),
        cell: (row) => <RelationshipStageBadge stage={row.relationshipStage} />,
        sortField: 'relationshipStage',
        card: 'secondary',
      },
      {
        key: 'innovationOwner',
        header: t('customer.columns.innovationOwner'),
        /* The name, not the id. Null only in the window between a user being
           deleted and an owner being reassigned — the API rejects a null on
           write, so this is a real but transient state, and it must read as
           "needs reassigning" rather than as a rendering failure. */
        cell: (row) =>
          row.innovationOwnerName ?? (
            <Badge tone="warn">{t('customer.columns.unassigned')}</Badge>
          ),
        card: 'secondary',
      },
      {
        key: 'commercialOwner',
        header: t('customer.columns.commercialOwner'),
        cell: (row) =>
          row.commercialOwnerName ?? (
            <Badge tone="warn">{t('customer.columns.unassigned')}</Badge>
          ),
        card: 'hidden',
      },
      {
        key: 'country',
        header: t('customer.columns.country'),
        cell: (row) => row.country ?? <span className="text-gray-400">—</span>,
        sortField: 'country',
        card: 'hidden',
      },
      {
        key: 'activity',
        header: t('customer.columns.activity'),
        /* Pilots and sites say whether the account is actually active rather
           than merely recorded — the difference between a customer and a
           contact list entry. */
        cell: (row) => (
          <span className="tabular-nums">
            {t('customer.columns.activityCounts', {
              pilots: formatNumber(row.pilotCount),
              assets: formatNumber(row.assetCount),
            })}
          </span>
        ),
        numeric: true,
        card: 'secondary',
      },
      {
        key: 'nextTouch',
        header: t('customer.columns.nextTouch'),
        cell: (row) =>
          row.nextTouchDate === null ? (
            <span className="text-gray-400">{t('customer.columns.noTouch')}</span>
          ) : (
            <span className="flex items-center justify-end gap-2">
              <span className="tabular-nums">{formatDate(row.nextTouchDate)}</span>
              {row.isTouchOverdue ? (
                <Badge tone="bad">{t('customer.overdue')}</Badge>
              ) : null}
            </span>
          ),
        sortField: 'nextTouchDate',
        numeric: true,
        card: 'secondary',
      },
    ],
    [t],
  );

  if (isError) {
    return (
      <p role="alert" className="text-sem-bad">
        {t('errors.generic')}
      </p>
    );
  }

  return (
    <>
      <PageHead
        kicker={t('nav.group.relationships')}
        title={t('customer.title')}
        {...(data === undefined
          ? {}
          : { lead: t('customer.count', { count: data.meta.total }) })}
      />

      <div className="mb-s3 flex flex-wrap items-center gap-3">
        <div className="min-w-0 flex-1">
          <CustomerFilters
            filters={state.filters}
            onFilterChange={setFilter}
            onClear={clearFilters}
          />
        </div>
        {/* Exports the on-screen filters and sort; hidden from Viewers (F18, §4). */}
        <ExportButton
          entity="customers"
          query={{
            ...state.filters,
            ...(state.sort === null
              ? {}
              : { sort: `${state.sort.field}:${state.sort.direction}` }),
          }}
        />
      </div>

      <DataTable
        rows={data?.data ?? []}
        columns={columns}
        rowKey={(row) => row.id}
        caption={t('customer.caption')}
        isLoading={isPending}
        sort={state.sort}
        onSortChange={toggleSort}
        emptyMessage={
          Object.keys(state.filters).length > 0
            ? t('customer.emptyFiltered')
            : t('customer.empty')
        }
        onRowSelect={(row) => {
          void navigate(customerPath(row.id));
        }}
      />

      {data === undefined ? null : (
        <Pagination
          page={data.meta.page}
          pageSize={data.meta.pageSize}
          total={data.meta.total}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      )}
    </>
  );
}
